import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, 
  Plus, 
  Edit2, 
  Trash2, 
  ArrowLeft,
  Mail,
  Building2,
  Layers,
  Shield,
  Eye,
  Key
} from 'lucide-react';

const ROLE_CONFIG = {
  admin: { label: 'Administrador', color: 'bg-blue-100 text-blue-700', icon: Shield },
  viewer: { label: 'Viewer', color: 'bg-slate-100 text-slate-700', icon: Eye }
};

export default function AdminUsuarios() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [filterEmpresa, setFilterEmpresa] = useState('');
  
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    role: 'viewer',
    empresa_id: '',
    marcas_ids: []
  });

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user, navigate]);

  // Calcular marcas filtradas como variable derivada, no estado
  const filteredMarcas = formData.empresa_id 
    ? marcas.filter(m => m.empresa_id === formData.empresa_id)
    : [];

  const loadData = async () => {
    try {
      const [usuariosRes, empresasRes, marcasRes] = await Promise.all([
        api.get('/admin/usuarios'),
        api.get('/empresas'),
        api.get('/marcas')
      ]);
      setUsuarios(usuariosRes.data);
      setEmpresas(empresasRes.data);
      setMarcas(marcasRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      email: '',
      password: '',
      role: 'viewer',
      empresa_id: '',
      marcas_ids: []
    });
    setEditingUser(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.empresa_id) {
      toast.error('Selecciona una empresa');
      return;
    }
    if (!formData.marcas_ids || formData.marcas_ids.length === 0) {
      toast.error('Selecciona al menos una marca');
      return;
    }
    if (!editingUser && !formData.password) {
      toast.error('La contraseña es requerida');
      return;
    }
    
    try {
      if (editingUser) {
        await api.put(`/admin/usuarios/${editingUser.id}`, {
          nombre: formData.nombre,
          role: formData.role,
          empresa_id: formData.empresa_id,
          marcas_ids: formData.marcas_ids
        });
        toast.success('Usuario actualizado');
      } else {
        await api.post('/admin/usuarios', formData);
        toast.success('Usuario creado');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleEdit = (usuario) => {
    setEditingUser(usuario);
    setFormData({
      nombre: usuario.nombre,
      email: usuario.email,
      password: '',
      role: usuario.role,
      empresa_id: usuario.empresa_id || '',
      marcas_ids: usuario.marcas_ids || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este usuario?')) return;
    try {
      await api.delete(`/admin/usuarios/${id}`);
      toast.success('Usuario eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleToggleMarca = (marcaId) => {
    setFormData(prev => {
      const current = prev.marcas_ids || [];
      if (current.includes(marcaId)) {
        return { ...prev, marcas_ids: current.filter(id => id !== marcaId) };
      } else {
        return { ...prev, marcas_ids: [...current, marcaId] };
      }
    });
  };

  const handleSelectAllMarcas = () => {
    const currentMarcas = formData.marcas_ids || [];
    if (currentMarcas.length === filteredMarcas.length && filteredMarcas.length > 0) {
      setFormData(prev => ({ ...prev, marcas_ids: [] }));
    } else {
      setFormData(prev => ({ ...prev, marcas_ids: filteredMarcas.map(m => m.id) }));
    }
  };

  const filteredUsuarios = filterEmpresa 
    ? usuarios.filter(u => u.empresa_id === filterEmpresa)
    : usuarios;

  return (
    <div data-testid="admin-usuarios">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-mobile">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 style={{ fontFamily: 'Outfit, sans-serif' }}>Usuarios</h1>
              <p className="text-xs text-muted-foreground">Gestión de accesos</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={resetForm}>
                <Plus className="w-4 h-4 mr-1" />
                Nuevo
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Nombre *</Label>
                  <Input
                    value={formData.nombre}
                    onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                    placeholder="Nombre completo"
                    required
                  />
                </div>
                
                <div>
                  <Label>Correo Electrónico *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="usuario@empresa.com"
                    required
                    disabled={!!editingUser}
                  />
                </div>
                
                {!editingUser && (
                  <div>
                    <Label>Contraseña *</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="Mínimo 6 caracteres"
                      required={!editingUser}
                    />
                  </div>
                )}
                
                <div>
                  <Label>Tipo de Usuario *</Label>
                  <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-blue-600" />
                          Administrador
                        </div>
                      </SelectItem>
                      <SelectItem value="viewer">
                        <div className="flex items-center gap-2">
                          <Eye className="w-4 h-4 text-slate-600" />
                          Viewer (Solo lectura)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Empresa *</Label>
                  <Select 
                    value={formData.empresa_id} 
                    onValueChange={(v) => setFormData({...formData, empresa_id: v, marcas_ids: []})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona empresa" />
                    </SelectTrigger>
                    <SelectContent>
                      {empresas.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            {emp.nombre_empresa}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.empresa_id && filteredMarcas.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Marcas con Acceso *</Label>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={handleSelectAllMarcas}
                      >
                        {(formData.marcas_ids || []).length === filteredMarcas.length ? 'Quitar todas' : 'Seleccionar todas'}
                      </Button>
                    </div>
                    <div className="space-y-2 p-3 bg-slate-50 rounded-lg max-h-48 overflow-y-auto">
                      {filteredMarcas.map((marca) => {
                        const isChecked = (formData.marcas_ids || []).includes(marca.id);
                        return (
                          <label 
                            key={marca.id} 
                            className="flex items-center gap-3 p-2 rounded hover:bg-white cursor-pointer"
                          >
                            <input 
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleToggleMarca(marca.id)}
                              className="w-4 h-4 rounded border-gray-300"
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium">{marca.nombre_marca}</p>
                              <p className="text-xs text-muted-foreground">{marca.plan}</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {(formData.marcas_ids || []).length} marca(s) seleccionada(s)
                    </p>
                  </div>
                )}
                
                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                    Cancelar
                  </Button>
                  <Button type="submit" className="flex-1">
                    {editingUser ? 'Guardar' : 'Crear'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="page-content">
        {/* Filtro por empresa */}
        <div className="mb-4">
          <Select value={filterEmpresa || "all"} onValueChange={(v) => setFilterEmpresa(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Todas las empresas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las empresas</SelectItem>
              {empresas.map((emp) => (
                <SelectItem key={emp.id} value={emp.id}>
                  {emp.nombre_empresa}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold">{filteredUsuarios.length}</p>
              <p className="text-xs text-muted-foreground">Usuarios</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold">
                {filteredUsuarios.filter(u => u.role === 'admin').length}
              </p>
              <p className="text-xs text-muted-foreground">Administradores</p>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => (
              <div key={i} className="h-24 bg-slate-200 rounded-lg animate-pulse"></div>
            ))}
          </div>
        ) : filteredUsuarios.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Sin usuarios</h3>
            <p className="text-muted-foreground mb-4">Crea el primer usuario para esta empresa</p>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Crear Usuario
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredUsuarios.map((usuario) => {
              const roleConfig = ROLE_CONFIG[usuario.role] || ROLE_CONFIG.viewer;
              const RoleIcon = roleConfig.icon;
              
              return (
                <Card key={usuario.id} className={usuario.activo === false ? 'opacity-60' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold truncate">{usuario.nombre}</h3>
                          <Badge className={roleConfig.color}>
                            <RoleIcon className="w-3 h-3 mr-1" />
                            {roleConfig.label}
                          </Badge>
                          {usuario.activo === false && (
                            <Badge variant="secondary">Inactivo</Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                          <Mail className="w-3 h-3" />
                          <span className="truncate">{usuario.email}</span>
                        </div>
                        
                        {usuario.empresa_nombre && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                            <Building2 className="w-3 h-3" />
                            <span>{usuario.empresa_nombre}</span>
                          </div>
                        )}
                        
                        {/* Marcas */}
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(usuario.marcas_nombres || []).map((nombre, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              <Layers className="w-3 h-3 mr-1" />
                              {nombre}
                            </Badge>
                          ))}
                          {(!usuario.marcas_nombres || usuario.marcas_nombres.length === 0) && usuario.marca_nombre && (
                            <Badge variant="outline" className="text-xs">
                              <Layers className="w-3 h-3 mr-1" />
                              {usuario.marca_nombre}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex gap-1 ml-2">
                        <Button size="icon" variant="ghost" onClick={() => handleEdit(usuario)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="text-destructive" 
                          onClick={() => handleDelete(usuario.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
